import React, { useCallback, useState } from 'react'
import { Upload, FileText, AlertCircle, X } from 'lucide-react'

interface FileUploadProps {
  onFileUpload: (file: File, content: Record<string, any>) => void
  accept?: string
  disabled?: boolean
  maxSize?: number // in MB
}

export default function FileUpload({ 
  onFileUpload, 
  accept = '.json', 
  disabled = false, 
  maxSize = 10 
}: FileUploadProps) {
  const [dragActive, setDragActive] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!file.name.toLowerCase().endsWith('.json')) {
      return 'Only JSON files are allowed'
    }

    // Check file size
    const fileSizeMB = file.size / (1024 * 1024)
    if (fileSizeMB > maxSize) {
      return `File size must be less than ${maxSize}MB`
    }

    return null
  }

  const processFile = async (file: File) => {
    setLoading(true)
    setError(null)

    try {
      const text = await file.text()
      const jsonContent = JSON.parse(text)

      // Basic validation for ComfyUI workflow
      if (typeof jsonContent !== 'object' || jsonContent === null) {
        throw new Error('Invalid JSON structure')
      }

      // Check if it's a valid ComfyUI workflow format
      const isApiFormat = Object.keys(jsonContent).some(key => 
        typeof jsonContent[key] === 'object' && 
        jsonContent[key]?.class_type
      )
      
      const isUiFormat = jsonContent.nodes && Array.isArray(jsonContent.nodes)

      if (!isApiFormat && !isUiFormat) {
        throw new Error('Invalid ComfyUI workflow format. Must be either API format or UI export format.')
      }

      setUploadedFile(file)
      onFileUpload(file, jsonContent)
    } catch (err: any) {
      setError(err.message || 'Failed to process file')
      setUploadedFile(null)
    } finally {
      setLoading(false)
    }
  }

  const handleFiles = useCallback(async (files: FileList) => {
    if (files.length === 0) return

    const file = files[0]
    const validationError = validateFile(file)
    
    if (validationError) {
      setError(validationError)
      return
    }

    await processFile(file)
  }, [maxSize])

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (disabled) return

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files)
    }
  }, [handleFiles, disabled])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    if (disabled) return
    
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files)
    }
  }, [handleFiles, disabled])

  const clearFile = () => {
    setUploadedFile(null)
    setError(null)
  }

  return (
    <div className="w-full">
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
          dragActive
            ? 'border-primary-400 bg-primary-50'
            : uploadedFile
            ? 'border-green-300 bg-green-50'
            : error
            ? 'border-red-300 bg-red-50'
            : 'border-gray-300 hover:border-gray-400'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept={accept}
          onChange={handleChange}
          disabled={disabled}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
        />

        <div className="text-center">
          {loading ? (
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mb-2"></div>
              <p className="text-sm text-gray-600">Processing file...</p>
            </div>
          ) : uploadedFile ? (
            <div className="flex flex-col items-center">
              <FileText className="h-8 w-8 text-green-500 mb-2" />
              <p className="text-sm font-medium text-green-700">{uploadedFile.name}</p>
              <p className="text-xs text-green-600">
                {(uploadedFile.size / 1024).toFixed(1)} KB
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  clearFile()
                }}
                className="mt-2 text-xs text-red-600 hover:text-red-800"
              >
                Remove file
              </button>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center">
              <AlertCircle className="h-8 w-8 text-red-500 mb-2" />
              <p className="text-sm font-medium text-red-700">Upload Error</p>
              <p className="text-xs text-red-600 mt-1">{error}</p>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setError(null)
                }}
                className="mt-2 text-xs text-red-600 hover:text-red-800"
              >
                Try again
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <Upload className="h-8 w-8 text-gray-400 mb-2" />
              <p className="text-sm font-medium text-gray-700">
                Drop your workflow file here, or click to browse
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Supports ComfyUI JSON files (max {maxSize}MB)
              </p>
            </div>
          )}
        </div>
      </div>

      {uploadedFile && !error && (
        <div className="mt-2 text-xs text-gray-500">
          ✓ File uploaded successfully. Ready to process.
        </div>
      )}
    </div>
  )
}